import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";

import { Button, Card, Textarea, notifyError, notifySuccess } from "../../../components/ui";
import { SectionFrame } from "../components/SectionFrame";
import { useRadarMutation, useSettings } from "../hooks/useRadarCorreo";
import { radarCorreoApi, apiErrorMessage } from "../services/radarCorreoService";

const toLines = (list?: string[]) => (list || []).join("\n");
const fromLines = (s: string) =>
  s
    .split(/[\n,;]+/)
    .map((x) => x.trim())
    .filter(Boolean);

const FIELDS = [
  { key: "ownDomains", hint: "ownDomainsHint" },
  { key: "ignoredDomains", hint: "ignoredDomainsHint" },
  { key: "carrierDomains", hint: "carrierDomainsHint" },
  { key: "alertRecipientIds", hint: "recipientsHint", label: "recipients" },
] as const;

export default function ConfiguracionSection() {
  const { t } = useTranslation("radarCorreo");
  const settings = useSettings();
  const [form, setForm] = useState<Record<string, string>>({});
  const save = useRadarMutation((input: Record<string, string[]>) => radarCorreoApi.saveSettings(input));
  const canEdit = Boolean(settings.data?.canEdit);

  useEffect(() => {
    if (!settings.data) return;
    setForm({
      ownDomains: toLines(settings.data.ownDomains),
      ignoredDomains: toLines(settings.data.ignoredDomains),
      carrierDomains: toLines(settings.data.carrierDomains),
      alertRecipientIds: toLines(settings.data.alertRecipientIds),
    });
  }, [settings.data]);

  return (
    <SectionFrame title={t("settings.title")} subtitle={t("settings.subtitle")} loading={settings.isLoading} error={settings.error} onRetry={() => void settings.refetch()}>
      <Card className="space-y-4">
        {!canEdit && <p className="rounded-lg bg-subtle p-2 text-xs text-content-secondary">{t("settings.readOnly")}</p>}
        <form
          className="space-y-4"
          onSubmit={(e) => {
            e.preventDefault();
            const input = Object.fromEntries(FIELDS.map((f) => [f.key, fromLines(form[f.key] || "")]));
            save.mutate(input, {
              onSuccess: () => void notifySuccess(t("common.saved")),
              onError: (err) => void notifyError(t("common.error"), apiErrorMessage(err) || undefined),
            });
          }}
        >
          {FIELDS.map((f) => (
            <Textarea
              key={f.key}
              label={t(`settings.${"label" in f ? f.label : f.key}`)}
              hint={t(`settings.${f.hint}`)}
              rows={3}
              placeholder={f.key === "alertRecipientIds" ? "" : t("settings.domainsPlaceholder")}
              value={form[f.key] || ""}
              disabled={!canEdit}
              onChange={(e) => setForm({ ...form, [f.key]: e.target.value })}
            />
          ))}
          {canEdit && (
            <div className="flex justify-end">
              <Button type="submit" loading={save.isPending}>
                {t("common.save")}
              </Button>
            </div>
          )}
        </form>
      </Card>
    </SectionFrame>
  );
}
